// pages/admins/permissions/PermissionUserDropdown.jsx
//
// Replaces the old always-visible, always-scrollable "Users" list panel on
// AccessManagementUsersPage.jsx with a compact searchable dropdown -- but
// keeps that page's original SERVER-SIDE filtering intact (search/role/
// department/designation/active, all resolved by GET /admin/access-
// management/users -- services/accessManagementService.js#listUsers).
// This component owns no filter STATE and makes no request itself: `users`
// is whatever the page's `fetchUsers(filters)` last returned, `filters` is
// the page's own filter state, and every filter control here just calls
// back into the page (onSearchChange/onSearchSubmit/onQuickFilterChange/
// onClearFilters) to update that state and re-fetch -- same endpoint, same
// params, same semantics as the page's original Filters panel, only the UI
// moved into this dropdown.
//
// ROLE_COLORS/colorForRole/initialsFor are intentionally duplicated (not
// imported) from AccessManagementUsersPage.jsx -- same precedent that file
// itself already follows relative to AccessManagementRolesPage.jsx: purely
// presentational helpers, never used for any access-control decision, kept
// local so this component stays a drop-in, self-contained piece.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Spinner } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";

const ROLE_COLORS = [
  { bg: "#f3e8ff", fg: "#7c3aed" },
  { bg: "#e0f2fe", fg: "#0369a1" },
  { bg: "#fef3c7", fg: "#92400e" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#fbeef1", fg: "#6d1530" },
  { bg: "#ffe4e6", fg: "#be123c" },
  { bg: "#fee2e2", fg: "#b91c1c" },
  { bg: "#ede9fe", fg: "#5b21b6" },
];
function colorForRole(roleName) {
  const key = String(roleName || "").trim().toLowerCase();
  if (!key) return ROLE_COLORS[4];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return ROLE_COLORS[hash % ROLE_COLORS.length];
}
function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// Same value convention the page's original Active/Inactive <Form.Select>
// used ("1"/"0"/"" sent straight through as the `is_active` query param) --
// preserved here so the filter payload sent to the server is unchanged.
const STATUS_CHOICES = [
  { key: "", label: "All Users" },
  { key: "1", label: "Active" },
  { key: "0", label: "Inactive" },
];

export default function PermissionUserDropdown({
  users, loading, error, onRetry,
  selectedUserId, selectedUser, onSelect,
  filters, onSearchChange, onSearchSubmit, onQuickFilterChange, onClearFilters,
  roleOptions, departmentOptions, designationOptions, orgColumnsAvailable,
}) {
  const [open, setOpen] = useState(false);
  // Keyboard-nav-only UI state -- never touches filter data. `hasNavigated`
  // disambiguates what Enter does in the search box: pressing Enter right
  // after typing re-queries the server (same as the page's original search
  // box); pressing Enter after Up/Down-ing to a row instead selects that
  // row. Reset whenever the result set changes so a stale highlight from a
  // previous search never gets "selected" by a later plain Enter.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasNavigated, setHasNavigated] = useState(false);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const selectedListRow = useMemo(
    () => users.find((u) => u.id === selectedUserId) || null,
    [users, selectedUserId]
  );

  const hasActiveFilters = !!(filters.search || filters.roleFilter || filters.departmentFilter || filters.designationFilter || filters.activeFilter);

  function closePanel() {
    setOpen(false);
    setHighlightedIndex(-1);
    setHasNavigated(false);
  }

  function openPanel() {
    setOpen(true);
    setHighlightedIndex(Math.max(0, users.findIndex((u) => u.id === selectedUserId)));
    setHasNavigated(false);
  }

  // Click-outside + Escape close the panel; focus never has to leave the
  // trigger/search input for either.
  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) closePanel();
    }
    function handleKeyDown(e) {
      if (e.key === "Escape") { e.preventDefault(); closePanel(); }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchInputRef.current?.focus();
  }, [open]);

  // A fresh result set (new search/filter results from the server) always
  // invalidates any previous keyboard highlight.
  useEffect(() => {
    setHighlightedIndex((idx) => (users.length === 0 ? -1 : Math.min(Math.max(idx, 0), users.length - 1)));
    setHasNavigated(false);
  }, [users]);

  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-row-index="${highlightedIndex}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  function handleSelect(userId) {
    onSelect(userId);
    closePanel();
  }

  function handleTriggerKeyDown(e) {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      openPanel();
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHasNavigated(true);
      setHighlightedIndex((idx) => Math.min(idx + 1, users.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHasNavigated(true);
      setHighlightedIndex((idx) => Math.max(idx - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hasNavigated) {
        const row = users[highlightedIndex];
        if (row) handleSelect(row.id);
      } else {
        onSearchSubmit();
      }
    }
  }

  // Trigger display: the already-selected row from `users` (available the
  // instant a row is clicked) layered with `selectedUser` (userDetail.user,
  // from the page's existing per-user /permissions fetch) for the unit/
  // office line -- that field only exists on the single-user detail
  // response, never on the bulk list, so it fills in once that call
  // resolves instead of blocking the trigger on it.
  const triggerName = selectedUser?.name ?? selectedListRow?.name;
  const triggerEmail = selectedUser?.email ?? selectedListRow?.email;
  const triggerRole = selectedUser?.roleName ?? selectedListRow?.role_name;
  const triggerUnit = selectedUser?.unitName;
  const triggerActive = selectedUser ? !!selectedUser.isActive : !!selectedListRow?.is_active;
  const triggerColor = colorForRole(triggerRole);

  return (
    <div className="pm-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`pm-dropdown-trigger${open ? " pm-dropdown-trigger-open" : ""}`}
        onClick={() => (open ? closePanel() : openPanel())}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedUserId && triggerName ? (
          <span className="pm-trigger-user">
            <span className="pm-dropdown-avatar" style={{ background: triggerColor.bg, color: triggerColor.fg }}>
              {initialsFor(triggerName)}
            </span>
            <span className="pm-trigger-text">
              <strong className="pm-truncate">{triggerName}</strong>
              <span className="pm-trigger-email pm-truncate">{triggerEmail}</span>
              <span className="pm-trigger-meta">
                {triggerRole || "—"}{triggerUnit ? ` • ${triggerUnit}` : ""}
              </span>
            </span>
            <Badge bg={null} className={triggerActive ? "unit-status pm-trigger-badge" : "unit-status unit-status-inactive pm-trigger-badge"}>
              {triggerActive ? "Active" : "Inactive"}
            </Badge>
          </span>
        ) : (
          <span className="pm-trigger-placeholder">Select user...</span>
        )}
        <FaChevronDown className="pm-dropdown-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="pm-dropdown-panel" role="listbox" aria-label="Select a user">
          <div className="pm-dropdown-search">
            <span className="pm-dropdown-search-icon" aria-hidden="true"><i className="fa-solid fa-magnifying-glass"></i></span>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, email or user ID..."
              value={filters.search}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search users"
            />
          </div>

          <div className="pm-dropdown-filters">
            <div className="pm-dropdown-status-group">
              {STATUS_CHOICES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`pm-dropdown-status-chip${filters.activeFilter === c.key ? " pm-dropdown-status-chip-active" : ""}`}
                  onClick={() => onQuickFilterChange("activeFilter", c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="pm-dropdown-filter-grid">
              {roleOptions?.length > 0 && (
                <div className="pm-dropdown-filter-field">
                  <label>Role</label>
                  <select
                    value={filters.roleFilter}
                    onChange={(e) => onQuickFilterChange("roleFilter", e.target.value)}
                    aria-label="Filter by role"
                  >
                    <option value="">All Roles</option>
                    {roleOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              )}
              {orgColumnsAvailable?.department && departmentOptions?.length > 0 && (
                <div className="pm-dropdown-filter-field">
                  <label>Department</label>
                  <select
                    value={filters.departmentFilter}
                    onChange={(e) => onQuickFilterChange("departmentFilter", e.target.value)}
                    aria-label="Filter by department"
                  >
                    <option value="">All Departments</option>
                    {departmentOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}
              {orgColumnsAvailable?.designation && designationOptions?.length > 0 && (
                <div className="pm-dropdown-filter-field pm-dropdown-filter-field-wide">
                  <label>Designation</label>
                  <select
                    value={filters.designationFilter}
                    onChange={(e) => onQuickFilterChange("designationFilter", e.target.value)}
                    aria-label="Filter by designation"
                  >
                    <option value="">All Designations</option>
                    {designationOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {hasActiveFilters && (
              <button type="button" className="pm-dropdown-clear" onClick={onClearFilters}>
                <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i> Clear filters
              </button>
            )}
          </div>

          <div className="pm-dropdown-list" ref={listRef}>
            {loading && <div className="am-empty pm-dropdown-empty"><Spinner animation="border" size="sm" /> Loading users…</div>}
            {!loading && error && (
              <div className="am-empty am-error pm-dropdown-empty">
                {error}
                {onRetry && (
                  <button type="button" className="unit-btn unit-btn-light" onClick={onRetry}>
                    <i className="fa-solid fa-rotate-right" aria-hidden="true"></i> Try Again
                  </button>
                )}
              </div>
            )}
            {!loading && !error && users.length === 0 && (
              <div className="am-empty pm-dropdown-empty">No users found.</div>
            )}
            {!loading && !error && users.map((u, index) => {
              const isSelected = u.id === selectedUserId;
              const isHighlighted = index === highlightedIndex;
              const c = colorForRole(u.role_name);
              return (
                <div
                  key={u.id}
                  data-row-index={index}
                  role="option"
                  aria-selected={isSelected}
                  className={`pm-dropdown-row${isSelected ? " pm-dropdown-row-selected" : ""}${isHighlighted ? " pm-dropdown-row-highlighted" : ""}`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(u.id)}
                >
                  <span className="pm-dropdown-avatar" style={{ background: c.bg, color: c.fg }}>
                    {initialsFor(u.name)}
                  </span>
                  <span className="pm-dropdown-row-body">
                    <span className="pm-dropdown-row-top">
                      <strong className="pm-truncate">{u.name}</strong>
                      <span className="pm-dropdown-row-role">{u.role_name || "—"}</span>
                    </span>
                    <span className="pm-dropdown-row-bottom">
                      <span className="pm-truncate">{u.email}</span>
                      <Badge bg={null} className={u.is_active ? "unit-status pm-dropdown-row-badge" : "unit-status unit-status-inactive pm-dropdown-row-badge"}>
                        {u.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
